import React from "react";
import { Box, Typography, Stack } from "@mui/material";
import moment from "moment";
import "moment/locale/en-gb"; // Ensure UK locale is active
import { useNavigate } from "react-router";

const getUpcomingWeek = (events) => {
  const week = [];

  for (let i = 0; i < 7; i++) {
    const currentDate = moment().add(i, "days");
    const formattedDate = currentDate.format("YYYY-MM-DD");

    // 3. Filter the events array to find ones that start on this specific day
    const eventsToday = events.filter((event) => {
      if (moment(event.eventDate).format("YYYY-MM-DD") === formattedDate) {
        return true;
      }

      // get events that have a start date before the current date and an end date after the current date
      if (event.eventDate && event.endDate) {
        const startDate = moment(event.eventDate).format("YYYY-MM-DD");
        const endDate = moment(event.endDate).format("YYYY-MM-DD");

        if (startDate <= formattedDate && formattedDate <= endDate) {
          return true;
        }
      }

      return false;
    });

    week.push({
      fullDate: formattedDate,
      dayName: currentDate.format("ddd"),
      dayNumber: currentDate.format("D"),
      eventCount: eventsToday.length, // 4. Count the matched events!
    });
  }
  return week;
};

function CalendarBar({ selectedDate, setSelectedDate, filteredEvents }) {
  const navigate = useNavigate();
  const weekData = getUpcomingWeek(filteredEvents);

  return (
    <Box
      sx={{
        mx: "auto",
        maxWidth: 700,
        backgroundColor: "#fff",
        borderRadius: 3,
        boxShadow: 2,
        p: { xs: 2, sm: 3 },
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          px: 0.5,
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: 800, fontSize: { xs: "1.1rem", sm: "1.3rem" } }}
        >
          Upcoming This Week
        </Typography>
        <Typography
          onClick={() =>
            navigate("/calendar", {
              state: { targetView: "month" },
            })
          }
          sx={{
            cursor: "pointer",
            fontWeight: 600,
            fontSize: { xs: "0.85rem", sm: "0.95rem" },
            color: "text.secondary",
            transition: "color 0.2s ease",
            "&:hover": { color: "text.primary", textDecoration: "underline" },
          }}
        >
          Full Calendar &rsaquo;
        </Typography>
      </Box>

      {/* Horizontal Scrollable Row */}
      <Stack
        direction="row"
        spacing={1}
        sx={{
          overflowX: "auto",
          pb: 1,
          // Hide scrollbar for Chrome, Safari and Opera
          "&::-webkit-scrollbar": { display: "none" },
          // Hide scrollbar for IE, Edge and Firefox
          msOverflowStyle: "none",
          scrollbarWidth: "none",
        }}
      >
        {weekData.map((day, index) => {
          const isActive = day.fullDate === selectedDate;

          return (
            <Box
              key={day.fullDate}
              onClick={() => setSelectedDate(day.fullDate)}
              sx={{
                minWidth: { xs: "70px", sm: "80px" },
                padding: "10px 4px",
                borderRadius: 2.5,
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                backgroundColor: isActive
                  ? "rgba(229, 169, 49, 0.08)"
                  : "transparent",
                border: "1px solid",
                borderColor: isActive
                  ? "rgba(229, 169, 49, 0.3)"
                  : "transparent",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: isActive
                    ? "rgba(229, 169, 49, 0.12)"
                    : "#f5f5f5",
                  transform: "translateY(-2px)",
                },
              }}
            >
              {/* Day Name */}
              <Typography
                sx={{
                  fontWeight: 600,
                  mb: 0.8,
                  fontSize: { xs: "0.75rem", sm: "0.85rem" },
                  color: isActive ? "#333" : "text.secondary",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                {index === 0 ? "Today" : day.dayName}
              </Typography>

              {/* Day Number Circle */}
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  backgroundColor: "transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 1,
                }}
              >
                <Typography
                  sx={{ fontWeight: 800, color: "#333", fontSize: "1.2rem" }}
                >
                  {day.dayNumber}
                </Typography>
              </Box>

              {/* Event Indicator Dot */}
              {day.eventCount > 0 && (
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    backgroundColor:
                      day.eventCount > 0
                        ? isActive
                          ? "#333"
                          : "#e5a931"
                        : "transparent",
                    mb: 0.5,
                  }}
                />
              )}

              {/* Event Count Text */}
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.7rem",
                  color: isActive ? "#333" : "text.secondary",
                  fontWeight: isActive ? 600 : 400,
                  opacity: day.eventCount > 0 ? 1 : 0, // Hide text if 0, but keep spacing
                }}
              >
                {day.eventCount === 1 ? "1 Event" : `${day.eventCount} Events`}
              </Typography>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}

export default CalendarBar;
